import { useEffect, useState } from "react";
import classNames from "classnames";
import styles from "./UpdatesFilters.module.scss";
import filterStyles from "../enhancedSearch/AppSearchFilters/AppSearchFilters.module.scss";
import colourStyles from "../enhancedSearch/SearchResults/EnhancedSearchResults.module.scss";
import { RANGE_KEYS } from "./updatesFilterUtils";
import DateRangeFilter, { Dropdown } from "./DateRangeFilter";
import useAppModernisation from "../../hooks/useAppModernisation";

const UpdatesFilters = ({
  filters,
  guidelineOptions,
  onChange,
  onOpenCustomRange,
}) => {
  const [guidelineOpen, setGuidelineOpen] = useState(false);
  const { isMobileView } = useAppModernisation();
  const [pendingGuidelines, setPendingGuidelines] = useState(filters.guidelines);

  useEffect(() => {
    if (guidelineOpen) setPendingGuidelines(filters.guidelines);
  }, [guidelineOpen, filters.guidelines]);

  useEffect(() => {
    if (!isMobileView) return;
    if (guidelineOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobileView, guidelineOpen]);

  const toggleGuideline = (code) => {
    if (isMobileView) {
      setPendingGuidelines((prev) => {
        const set = new Set(prev);
        if (set.has(code)) set.delete(code);
        else set.add(code);
        return Array.from(set);
      });
      return;
    }
    const set = new Set(filters.guidelines);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    onChange({ ...filters, guidelines: Array.from(set) });
  };

  const selectAllGuidelines = () => {
    if (isMobileView) {
      setPendingGuidelines([]);
      return;
    }
    onChange({ ...filters, guidelines: [] });
    setGuidelineOpen(false);
  };

  const applyGuidelines = () => {
    onChange({ ...filters, guidelines: pendingGuidelines });
    setGuidelineOpen(false);
  };

  const guidelineButtonLabel =
    filters.guidelines.length === 0
      ? "All Guidelines"
      : filters.guidelines.length === 1
        ? guidelineOptions.find((g) => g.code === filters.guidelines[0])?.label ||
          filters.guidelines[0]
        : `${filters.guidelines.length} Guidelines`;

  const hasActiveFilters =
    filters.range !== RANGE_KEYS.ALL || filters.guidelines.length > 0;

  const clearAll = () => {
    setGuidelineOpen(false);
    onChange({
      range: RANGE_KEYS.ALL,
      from: null,
      to: null,
      guidelines: [],
    });
  };

  return (
    <div className={styles.filterBar}>
      <span className={classNames("size-14 regular", styles.filterLabel)}>
        Filter by:
      </span>

      <DateRangeFilter
        filters={filters}
        onChange={onChange}
        onOpenCustomRange={onOpenCustomRange}
      />

      {isMobileView ? (
        <>
          <button
            type="button"
            className={classNames("size-14 regular", styles.dropdownButton)}
            aria-haspopup="dialog"
            aria-expanded={guidelineOpen}
            aria-label="Filter by guideline"
            onClick={() => setGuidelineOpen(true)}
          >
            <span>{guidelineButtonLabel}</span>
            <i className={classNames("tg-icons-ChevronDown", styles.caretIcon)}></i>
          </button>
          {guidelineOpen ? (
            <div
              className={styles.sheetBackdrop}
              role="dialog"
              aria-modal="true"
              aria-label="Filter by guideline"
              onClick={() => setGuidelineOpen(false)}
            >
              <div
                className={styles.sheet}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.sheetHeader}>
                  <span className={classNames("size-16 medium")}>
                    Filter by guideline
                  </span>
                  <button
                    type="button"
                    className={styles.sheetClose}
                    aria-label="Close"
                    onClick={() => setGuidelineOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className={styles.sheetBody}>
                  <div
                    role="option"
                    aria-selected={pendingGuidelines.length === 0}
                    className={classNames(
                      filterStyles.guidelineListItem,
                      styles.allGuidelinesItem,
                    )}
                  >
                    <label className={filterStyles.guidelineContainer}>
                      <div className={filterStyles.counter}>
                        <input
                          type="checkbox"
                          name="guideline"
                          checked={pendingGuidelines.length === 0}
                          onChange={selectAllGuidelines}
                          className={filterStyles.customCheckbox}
                        />
                      </div>
                      <div className="size-14 medium">All Guidelines</div>
                    </label>
                  </div>
                  <div className={styles.menuDivider} />
                  <ul className={filterStyles.guidelineList}>
                    {guidelineOptions.map(({ code, label }) => {
                      const checked = pendingGuidelines.includes(code);
                      const iconClassName = label.replace(/ /g, "").toLowerCase();
                      return (
                        <li
                          key={code}
                          className={filterStyles.guidelineListItem}
                          role="option"
                          aria-selected={checked}
                        >
                          <label className={filterStyles.guidelineContainer}>
                             <div className={filterStyles.counter}>
                              <input
                                type="checkbox"
                                name="guideline"
                                value={code}
                                checked={checked}
                                onChange={() => toggleGuideline(code)}
                                className={filterStyles.customCheckbox}
                              />
                            </div>
                            <div
                              className={classNames(
                                colourStyles[iconClassName],
                                filterStyles.guidelineIcon,
                              )}
                            />
                            <div className="size-14 medium">{label}</div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <div className={styles.sheetApplyBar}>
                  <button
                    type="button"
                    onClick={applyGuidelines}
                    className={filterStyles.showResultsButton}
                  >
                    Apply filters
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <Dropdown
          ariaLabel="Filter by guideline"
          buttonLabel={guidelineButtonLabel}
          isOpen={guidelineOpen}
          setOpen={setGuidelineOpen}
        >
          <div
            role="option"
            aria-selected={filters.guidelines.length === 0}
            className={classNames(
              "size-14 regular",
              styles.menuItem,
              filters.guidelines.length === 0 && styles.menuItemActive,
            )}
            onClick={selectAllGuidelines}
          >
            All Guidelines
          </div>
          <div className={styles.menuDivider} />
          <ul className={filterStyles.guidelineList}>
            {guidelineOptions.map(({ code, label }) => {
              const checked = filters.guidelines.includes(code);
              const iconClassName = label.replace(/ /g, "").toLowerCase();
              return (
                <li
                  key={code}
                  className={filterStyles.guidelineListItem}
                  role="option"
                  aria-selected={checked}
                >
                  <label className={filterStyles.guidelineContainer}>
                    <div
                      className={classNames(
                        colourStyles[iconClassName],
                        filterStyles.guidelineIcon,
                      )}
                    />
                    <div className="size-14 medium">{label}</div>
                    <div className={filterStyles.counter}>
                      <input
                        type="checkbox"
                        name="guideline"
                        value={code}
                        checked={checked}
                        onChange={() => toggleGuideline(code)}
                        className={filterStyles.customCheckbox}
                      />
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </Dropdown>
      )}

      {hasActiveFilters ? (
        <button
          type="button"
          className={styles.clearButton}
          aria-label="Clear filters"
          onClick={clearAll}
        >
          <i className="tg-icons-Cross"></i>
          <span>Clear Filters</span>
        </button>
      ) : null}
    </div>
  );
};

export default UpdatesFilters;
